import { useEffect, useRef, useState } from "react";
import { read_activity_timeline_events } from "./timeline_normalization";

const empty_page = { events: [], nextCursor: "", hasMore: false, loading: false, loadingMore: false, error: "", warning: "", publication: { state: "synced" } };

// All apps consume the same projection; no local rows, text dedupe, or polling.
export function useTimelinePages({ identity, readPage, refreshKey, t }) {
    const [page, setPage] = useState(empty_page);
    const request = useRef({ version: 0, busy: false, cursor: "", failedCursor: "" });
    const readers = useRef({});
    readers.current = { identity, readPage, t };

    const load_page = async (cursor = "") => {
        const state = request.current;
        if (!readers.current.identity || (cursor && state.busy)) return;
        const version = cursor ? state.version : ++state.version;
        state.busy = true;
        setPage(previous => ({ ...(cursor ? previous : empty_page), loading: !cursor, loadingMore: Boolean(cursor), error: "" }));
        try {
            const result = await readers.current.readPage(cursor);
            if (version !== request.current.version) return;
            if (result?.error) throw result.error;
            if (result?.integration?.niffler === "unavailable") throw new Error(result.warning || "Timeline is temporarily unavailable.");
            request.current.cursor = result?.nextCursor || "";
            request.current.failedCursor = "";
            setPage(previous => ({
                ...empty_page, ...result,
                events: read_activity_timeline_events([...(cursor ? previous.events : []), ...(result?.events || [])]),
                loading: false, loadingMore: false, error: ""
            }));
        } catch (error) {
            if (version !== request.current.version) return;
            const code = error?.response?.data?.code || error?.code;
            if (cursor && code === "TIMELINE_SNAPSHOT_CHANGED") {
                request.current.busy = false;
                load_page("");
                return;
            }
            request.current.failedCursor = cursor;
            setPage(previous => ({ ...previous, loading: false, loadingMore: false,
                error: error?.response?.data?.message || error?.message || readers.current.t("Timeline is temporarily unavailable.") }));
        } finally {
            if (version === request.current.version) request.current.busy = false;
        }
    };

    const refresh = () => load_page("");
    const retry = () => load_page(request.current.failedCursor);
    const load_more = () => {
        if (request.current.cursor) load_page(request.current.cursor);
    };

    useEffect(() => {
        request.current.cursor = "";
        request.current.failedCursor = "";
        request.current.busy = false;
        if (identity) load_page("");
        else setPage(empty_page);
        const on_focus = () => {
            if (document.visibilityState !== "hidden" && !request.current.busy) load_page("");
        };
        window.addEventListener("focus", on_focus);
        document.addEventListener("visibilitychange", on_focus);
        return () => {
            request.current.version++;
            window.removeEventListener("focus", on_focus);
            document.removeEventListener("visibilitychange", on_focus);
        };
    }, [identity, refreshKey]);

    return { ...page, refresh, retry, load_more };
}
